import { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  registerables,
  type ChartType,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";

import { formatValue, type KpiFormat } from "../lib/format";
import { compactCurrency, compactInteger } from "../lib/compactNumber.mjs";
import { humanize } from "../lib/labels";
import {
  referenceLineBounds,
  referenceLinePlugin,
  type ResolvedReferenceLine,
} from "../lib/referenceLines.mjs";

// Chart.js v3+ requires explicit registration of both elements
// (BarElement, LineElement, ...) AND controllers (BarController,
// LineController, ...) before any chart will mount. registerables
// is the convenience bundle that exports both. Without this the
// renderer throws "bar is not a registered controller" mid-mount
// and React unmounts the tree.
ChartJS.register(...registerables);

export type SupportedChartType = "bar" | "stackedBar" | "line" | "lineArea" | "pie";
export type AxisGranularity = "monthly" | "weekly" | "daily" | "category";

export interface ChartProps {
  type: SupportedChartType;
  data: ChartData;
  options?: ChartOptions;
  height?: number;
  /** MER-161: drives Y-axis tick formatting (currency_usd -> "$50k",
   * percent -> "15%", etc.) and the beginAtZero policy. */
  format?: KpiFormat;
  /** MER-161: drives X-axis tick formatting. `monthly` collapses
   * "2026-04-01" to "Apr 26", `weekly` to "Apr 06", etc. */
  xAxisGranularity?: AxisGranularity;
  /** MER-170: accessible name announced to screen readers via the
   * canvas aria-label. Wired from `widget.name` in ChartWidget. */
  name?: string;
  /** MER-605: horizontal target lines in CHART DATA units, already
   * converted from the manifest's display units by
   * `resolveReferenceLines`. Drawn over the datasets, and the y scale is
   * widened so a line outside the data range still lands inside the plot. */
  referenceLines?: ResolvedReferenceLine[];
}

// MER-161 brand palette. Brand-deep + brand-teal are the hex values
// from the wrapper mock; the canonical CSS tokens live in
// dashboard/src/styles/brand.css. Inline here because Chart.js's
// scriptable options run in canvas context where CSS vars aren't
// resolvable without computeStyle hops.
const BRAND_DEEP = "#0F6B5C";
const BRAND_MINT = "#A8E0D4";
const TEXT_MUTED = "#95A5A6";

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Inter", "Helvetica Neue", Arial, sans-serif';

// Y-axis tick callback. Big numbers are compacted by lib/compactNumber.mjs
// so $1,250,000 reads as "$1.25M" not as a 9-digit wall.
function tickForFormat(value: number, format: KpiFormat): string {
  switch (format) {
    case "currency_usd":
      return compactCurrency(value);
    case "percent":
      return `${(value * 100).toFixed(0)}%`;
    case "integer":
      return compactInteger(value);
    case "decimal_1":
      return value.toFixed(1);
    case "decimal_2":
      return value.toFixed(2);
    case "ratio":
      return `${value.toFixed(1)}x`;
    case "days":
      return `${value.toFixed(0)}d`;
    default:
      // Fallback to the standard formatter (returns "n/a" for null/NaN).
      return formatValue(value, format);
  }
}

// X-axis tick formatter. Chart.js passes the tick *index* and the
// underlying labels array; this returns the original label
// re-formatted by granularity.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatXTick(raw: string, granularity: AxisGranularity): string {
  // MER-438: a category axis (division, LOB, etc.) rendered raw snake_case
  // ("central_coast", "front_load"). Humanize so the customer sees
  // "Central Coast" / "Front Load". Idempotent for already-clean labels.
  if (granularity === "category") {
    // MER-441: a raw ISO timestamp ("2026-06-07T23:03:31.9+00:00") that slipped
    // onto a category axis renders as a readable date, not mangled text.
    const ts = /^(\d{4})-(\d{2})-(\d{2})T/.exec(raw);
    if (ts) return `${MONTHS[parseInt(ts[2], 10) - 1] ?? ts[2]} ${parseInt(ts[3], 10)}`;
    return humanize(raw);
  }
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(raw);
  if (!m) return raw;
  const year = m[1].slice(2);
  const month = MONTHS[parseInt(m[2], 10) - 1] ?? m[2];
  const day = m[3];
  if (granularity === "monthly") return `${month} ${year}`;
  if (!day) return `${month} ${year}`;
  return `${month} ${parseInt(day, 10)}`;
}

function beginAtZeroFor(format: KpiFormat | undefined): boolean {
  // Currency / integer / count always start at zero so magnitudes are
  // comparable. Percent / ratio / decimal_* let the chart auto-scale
  // so 60-80% margin variations aren't squashed against the top edge.
  if (!format) return true;
  return format === "currency_usd" || format === "integer";
}

function meridianChartOptions(
  format: KpiFormat | undefined,
  xAxisGranularity: AxisGranularity | undefined,
): ChartOptions {
  const granularity = xAxisGranularity ?? "category";
  return {
    plugins: {
      legend: {
        labels: {
          font: {
            family: FONT_STACK,
            size: 11,
            weight: 600,
          },
          color: TEXT_MUTED,
          boxWidth: 12,
          boxHeight: 12,
          padding: 12,
          usePointStyle: true,
          pointStyle: "rectRounded",
        },
      },
      tooltip: {
        backgroundColor: BRAND_DEEP,
        titleColor: "#ffffff",
        bodyColor: BRAND_MINT,
        borderColor: BRAND_MINT,
        borderWidth: 1,
        cornerRadius: 6,
        padding: 10,
        titleFont: {
          family: FONT_STACK,
          size: 12,
          weight: 700,
        },
        bodyFont: {
          family: FONT_STACK,
          size: 12,
          weight: 500,
        },
        callbacks: format
          ? {
              label: (ctx) => {
                const label = ctx.dataset.label ? `${ctx.dataset.label}: ` : "";
                const v =
                  typeof ctx.parsed === "object" && ctx.parsed !== null
                    ? (ctx.parsed as { y?: number }).y
                    : (ctx.parsed as number);
                if (v === null || v === undefined) return `${label}n/a`;
                return `${label}${formatValue(v, format)}`;
              },
            }
          : undefined,
      },
    },
    scales: {
      x: {
        ticks: {
          font: { family: FONT_STACK, size: 10 },
          color: TEXT_MUTED,
          callback: function (_value, index) {
            // `this.getLabelForValue(index)` returns the raw label.
            // Re-format per granularity. eslint-disable-next-line
            // @typescript-eslint/no-explicit-any covers the chart.js
            // `this` typing.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const raw = (this as any).getLabelForValue(index);
            return formatXTick(String(raw), granularity);
          },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: beginAtZeroFor(format),
        ticks: {
          font: { family: FONT_STACK, size: 10 },
          color: TEXT_MUTED,
          callback: format
            ? (value) => {
                const n = typeof value === "number" ? value : Number(value);
                if (!Number.isFinite(n)) return String(value);
                return tickForFormat(n, format);
              }
            : undefined,
        },
        grid: { color: "#EEF1F4" },
      },
    },
  };
}

/**
 * Thin Chart.js wrapper. The renderer maps manifest chart_type to one
 * of the supported types (stackedBar comes from chart_type=stacked_bar).
 *
 * MER-161: applies the Meridian brand preset (legend / tooltip / tick
 * formatting / beginAtZero policy). Per-widget `options` still
 * overrides the preset for chart-specific tweaks.
 */
export default function Chart(props: ChartProps) {
  const {
    type,
    data,
    options,
    height = 280,
    format,
    xAxisGranularity,
    name,
    referenceLines,
  } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    // Defensive: if React Strict Mode (dev) double-invoked the effect
    // and the previous Chart instance is still attached to this canvas,
    // tear it down before constructing the new one. The cleanup
    // function below handles the normal case; this handles the
    // double-mount sequence that causes "Canvas is already in use".
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // stackedBar collapses to "bar" with stacked scales.
    // lineArea collapses to "line" with fill: true on each dataset.
    const baseType: ChartType =
      type === "stackedBar" ? "bar" : type === "lineArea" ? "line" : (type as ChartType);
    const stackedOptions: ChartOptions =
      type === "stackedBar"
        ? {
            scales: {
              x: { stacked: true },
              y: { stacked: true },
            },
          }
        : {};

    const brandPreset = meridianChartOptions(format, xAxisGranularity);
    // Merge: brand preset first, stacked scales second (overrides x/y
    // stacked flag without losing brand tick callbacks), explicit
    // per-widget options last so widget authors can still override
    // anything. Scales merge deeply because shallow spread would wipe
    // out the brand callbacks. Chart.js's scales type is a
    // discriminated union over scale.type which TS can't narrow across
    // a hand-rolled merge, so we cast to ChartOptions["scales"].
    // MER-605: widen the y scale so a declared reference line is inside
    // the plot area even when no data point reaches it (Heath's
    // break-even at 100% during a month that recovers 80%). Suggested
    // bounds only, and still overridable by per-widget options.
    const lines = referenceLines ?? [];
    const refBounds = referenceLineBounds(lines) ?? {};
    const mergedScales = {
      x: { ...brandPreset.scales?.x, ...stackedOptions.scales?.x, ...options?.scales?.x },
      y: {
        ...brandPreset.scales?.y,
        ...refBounds,
        ...stackedOptions.scales?.y,
        ...options?.scales?.y,
      },
    } as ChartOptions["scales"];
    const merged: ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      ...brandPreset,
      ...stackedOptions,
      ...options,
      plugins: {
        ...brandPreset.plugins,
        ...(options?.plugins ?? {}),
      },
      scales: mergedScales,
    };
    // MER-462: pie is radial; the brand preset's cartesian scales would
    // make Chart.js draw a bare 0.00-1.00 x/y axis frame around (or
    // instead of) the pie. Strip them for pie type.
    if (baseType === "pie") {
      delete (merged as { scales?: unknown }).scales;
    }

    const dataForChart: ChartData =
      type === "lineArea"
        ? {
            ...data,
            datasets: data.datasets.map((ds) => ({ ...ds, fill: true })),
          }
        : data;

    // MER-605: pie has no cartesian y scale, so a reference line has
    // nowhere to sit; skip the plugin there rather than draw at NaN.
    const inlinePlugins: Plugin[] =
      lines.length > 0 && baseType !== "pie" ? [referenceLinePlugin(lines, FONT_STACK)] : [];

    chartRef.current = new ChartJS(ctx, {
      type: baseType,
      data: dataForChart,
      options: merged,
      plugins: inlinePlugins,
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [type, data, options, format, xAxisGranularity, referenceLines]);

  return (
    <div style={{ height }}>
      <canvas ref={canvasRef} role="img" aria-label={name ? `Chart: ${name}` : "Chart"} />
    </div>
  );
}
